/**
 * Where `Treemap`'s tiles go and what each one can print — pure geometry, so
 * every rule here is tested without a DOM (jsdom has no layout to test it in).
 */

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

/**
 * Worst aspect ratio in a candidate row, given the side it is laid along.
 * Bruls, Huizing & van Wijk (2000) — the row keeps taking tiles while this
 * number falls, and closes as soon as adding one would make it worse.
 */
const worst = (row: readonly number[], side: number, sum: number): number => {
  if (sum <= 0 || side <= 0) return Infinity
  let min = Infinity
  let max = 0
  for (const area of row) {
    if (area < min) min = area
    if (area > max) max = area
  }
  if (min <= 0) return Infinity
  const s2 = sum * sum
  const side2 = side * side
  return Math.max((side2 * max) / s2, s2 / (side2 * min))
}

/**
 * Squarified treemap, by hand.
 *
 * Recharts ships a Treemap, but it renders its own `<rect>` tree with inline
 * fills — there is no seam to hang a token class on, so every tile would need
 * a hard-coded colour. Forty lines of layout buys tiles that are real
 * elements: `<button>` when they are pressable, a token class for the band,
 * and text that the browser lays out rather than an SVG `<text>` that cannot
 * wrap.
 *
 * Rows are laid along the shorter side of the space that is left, which is
 * what keeps tiles near square rather than long slivers.
 */
export const squarify = (areas: readonly number[], box: Rect): Rect[] => {
  const out: Rect[] = []
  let { x, y, w, h } = box
  let i = 0

  while (i < areas.length) {
    const side = Math.min(w, h)
    const row: number[] = []
    let sum = 0

    while (i < areas.length) {
      const next = areas[i] ?? 0
      if (row.length > 0 && worst([...row, next], side, sum + next) > worst(row, side, sum)) break
      row.push(next)
      sum += next
      i += 1
    }

    const thickness = side > 0 ? sum / side : 0
    let offset = 0
    for (const area of row) {
      const length = sum > 0 ? (area / sum) * side : 0
      out.push(
        w >= h
          ? { x, y: y + offset, w: thickness, h: length }
          : { x: x + offset, y, w: length, h: thickness },
      )
      offset += length
    }

    if (w >= h) {
      x += thickness
      w -= thickness
    } else {
      y += thickness
      h -= thickness
    }
  }

  return out
}

/** Weights scaled to fill `width × height`, squarified — `Treemap`'s default. */
export const layoutSquarified = (weights: readonly number[], width: number, height: number) => {
  const total = weights.reduce((acc, weight) => acc + Math.max(weight, 0), 0)
  const scale = total > 0 ? (width * height) / total : 0
  return squarify(
    weights.map((weight) => Math.max(weight, 0) * scale),
    { x: 0, y: 0, w: width, h: height },
  )
}

/** Below this a tile is not something a pointer can land on. */
const TINY = 0.5

const aspect = (w: number, h: number) => {
  const a = Math.max(w, TINY)
  const b = Math.max(h, TINY)
  return a > b ? a / b : b / a
}

/** Rows longer than this cannot give anyone a tile to read; capping them keeps the search small. */
const MAX_ROW = 64

interface Plan {
  worst: number
  total: number
  from: number
}

/** Running totals of `areas`, negatives read as 0: `prefix[k]` is the sum of the first `k`. */
const prefixSums = (areas: readonly number[]): number[] => {
  const prefix = [0]
  for (const area of areas) prefix.push((prefix[prefix.length - 1] ?? 0) + Math.max(area, 0))
  return prefix
}

/**
 * Lays `rows` — `[from, to)` ranges of `areas`, in order — top to bottom
 * across `box`, each row the full width and as tall as its share of the box.
 */
const placeRows = (
  areas: readonly number[],
  box: Rect,
  rows: readonly (readonly [number, number])[],
  prefix: readonly number[] = prefixSums(areas),
): Rect[] => {
  const out: Rect[] = []
  let y = box.y
  rows.forEach(([from, to], index) => {
    const rowSum = (prefix[to] ?? 0) - (prefix[from] ?? 0)
    // The last row and the last tile in each row take whatever floating
    // point left over, so the map always ends flush with the box.
    const height = index === rows.length - 1 ? box.y + box.h - y : box.w > 0 ? rowSum / box.w : 0
    let x = box.x
    for (let k = from; k < to; k += 1) {
      const width =
        k === to - 1
          ? box.x + box.w - x
          : rowSum > 0
            ? (Math.max(areas[k] ?? 0, 0) / rowSum) * box.w
            : 0
      out.push({ x, y, w: width, h: height })
      x += width
    }
    y += height
  })

  return out
}

/**
 * Tiles in full-width rows, top to bottom, biggest first — the arrangement
 * both V2 heatmap frames draw (`4243:106729`, `4243:118584`), where
 * `squarify` opens with a column down the left of a landscape box and then
 * flips orientation strip by strip.
 *
 * `areas` must already be sorted, biggest first. Where each row ends is the
 * one free choice, and it is made for the whole map at once rather than
 * greedily. A greedy strip only looks at the row in hand, so the last row
 * inherits whatever area is left over: on the design's own numbers that is a
 * 3px-tall sliver the width of the map. This tries every set of breaks
 * (dynamic programming over the order) and keeps the one whose WORST tile is
 * closest to square, then the lowest sum over all tiles, so one bad tile
 * cannot hide behind many good ones.
 *
 * `gap` is the gutter each tile will lose on its right and bottom edge, and
 * the ratios are judged on what is left, because that is what a reader sees.
 */
export const layoutRows = (areas: readonly number[], box: Rect, gap = 0): Rect[] => {
  const n = areas.length
  if (n === 0) return []

  const prefix = prefixSums(areas)

  const plans: Plan[] = [{ worst: 0, total: 0, from: 0 }]
  for (let end = 1; end <= n; end += 1) {
    let pick: Plan = { worst: Infinity, total: Infinity, from: end - 1 }

    for (let start = end - 1; start >= Math.max(0, end - MAX_ROW); start -= 1) {
      const before = plans[start]
      const rowSum = (prefix[end] ?? 0) - (prefix[start] ?? 0)
      if (before === undefined || rowSum <= 0 || box.w <= 0) continue

      const height = rowSum / box.w - gap
      let rowWorst = 0
      let rowTotal = 0
      for (let k = start; k < end; k += 1) {
        const ratio = aspect((Math.max(areas[k] ?? 0, 0) / rowSum) * box.w - gap, height)
        if (ratio > rowWorst) rowWorst = ratio
        rowTotal += ratio
      }

      const candidate = {
        worst: Math.max(before.worst, rowWorst),
        total: before.total + rowTotal,
        from: start,
      }
      const tied = Math.abs(candidate.worst - pick.worst) <= 1e-9
      if (candidate.worst < pick.worst - 1e-9 || (tied && candidate.total < pick.total)) {
        pick = candidate
      }
    }

    plans.push(pick)
  }

  const rows: [number, number][] = []
  for (let end = n; end > 0;) {
    const from = plans[end]?.from ?? end - 1
    rows.unshift([from, end])
    end = from
  }

  return placeRows(areas, box, rows, prefix)
}

/**
 * Raises every area below `min` to `min` and takes the difference back from
 * the rest in proportion, so the total — and so the box — does not change,
 * and the order does not either.
 *
 * `min` is capped at an even split: a map with more names than room gives
 * every name the same tile rather than overflowing.
 */
export const floorAreas = (areas: readonly number[], min: number): number[] => {
  const clean = areas.map((area) => Math.max(area, 0))
  const total = clean.reduce((acc, area) => acc + area, 0)
  if (clean.length === 0 || total <= 0 || min <= 0) return clean

  const floor = Math.min(min, total / clean.length)
  const floored = new Set<number>()

  for (;;) {
    const restTotal = total - floor * floored.size
    const restRaw = clean.reduce((acc, area, i) => (floored.has(i) ? acc : acc + area), 0)
    const scale = restRaw > 0 ? restTotal / restRaw : 0

    let grew = false
    for (let i = 0; i < clean.length; i += 1) {
      if (!floored.has(i) && (clean[i] ?? 0) * scale < floor) {
        floored.add(i)
        grew = true
      }
    }

    if (!grew) return clean.map((area, i) => (floored.has(i) ? floor : area * scale))
  }
}

/**
 * The smallest tile the market heatmap draws: a 20px slot (16px of colour
 * once the gutter is taken) or a twentieth of an average tile, whichever is
 * bigger.
 *
 * A name with no figure on record comes in with a weight of 0 so that it
 * stays on the map, and so does one whose figure is a sliver of its
 * neighbours': at map scale either is a fraction of a pixel — a link nobody
 * can see that still takes a Tab stop. A floor on area alone is not
 * enough in a small group: three names that traded and two that did not
 * leave the two sharing a tall row as 0.8px-wide slivers, whatever the row
 * breaks. The share of an average tile gives them room to be drawn. Flooring
 * is the trade: such a name is drawn a little bigger than its figure, never
 * invisibly. Neither V2 frame's numbers come near either floor.
 */
const MIN_TILE_SIDE = 20
const MIN_TILE_SHARE = 0.05

/**
 * The market heatmap's layout: `layoutRows` over floored weights, in a box
 * `gap` wider and taller than the map, so that the gutter each tile loses on
 * its right and bottom leaves the last column and the last row flush with
 * the map's own edge, as both frames draw them. Positions come back in that
 * grown box.
 *
 * A map where no tile has any weight — every figure missing under the chosen
 * sizing — is drawn as equal tiles. There is no ratio to draw, and without
 * this the rows collapse to nothing and the last tile takes the whole box.
 */
export const layoutFlushRows = (
  given: readonly number[],
  width: number,
  height: number,
  gap: number,
): Rect[] => {
  const box = { x: 0, y: 0, w: width + gap, h: height + gap }
  const weights = given.some((weight) => weight > 0) ? given : given.map(() => 1)
  const total = weights.reduce((acc, weight) => acc + Math.max(weight, 0), 0)
  const scale = total > 0 ? (box.w * box.h) / total : 0
  const floor = Math.max(
    MIN_TILE_SIDE * MIN_TILE_SIDE,
    weights.length > 0 ? (MIN_TILE_SHARE * box.w * box.h) / weights.length : 0,
  )
  const areas = floorAreas(
    weights.map((weight) => Math.max(weight, 0) * scale),
    floor,
  )
  return layoutRows(areas, box, gap)
}

/** What a tile prints: its name and figure, its name alone, or nothing (words move to `sr-only`). */
export type TilePrint = 'all' | 'label' | 'none'

export type HeatmapLabel =
  { print: 'all'; label: string; value: string; lines: 1 | 2 } | { print: 'label' | 'none' }

interface Rung {
  /** Type classes for the name and the figure. */
  label: string
  value: string
  labelPx: number
  valuePx: number
  labelLine: number
  valueLine: number
  minW: number
  minH: number
  maxLines: 1 | 2
}

/**
 * The heatmap's three type steps. The V2 frames measured 18/14px, 14/12px and
 * 11.5/11px; the 2026-10-05 type scale snaps them to 20/14, 14/12 and 12/12,
 * which is also what the revamp reference sets its big tiles at (20/600 over
 * 14/400). The middle step's figure sits 4px under the name (4-grid; was
 * 2px), and `valueLine` counts that 4px. The pixel figures here are what the
 * fit is measured with, so they follow the classes' sizes in globals.css.
 *
 * The frames do not pick a step by size alone. EBL gets the big step in a
 * 136px tile and "Microfinance" does not in a 133px one, because the name
 * would not fit; "Life insurance" and "Development banks" wrap onto a second
 * line at the middle step instead of shrinking. So a step is taken when the
 * tile is big enough AND the name fits in its lines — which reproduces the
 * frames' choice for every tile in both.
 */
const HEATMAP_RUNGS: readonly Rung[] = [
  {
    label: 'text-h-section',
    value: 'text-body-m',
    labelPx: 20,
    valuePx: 14,
    labelLine: 28,
    valueLine: 20,
    minW: 130,
    minH: 120,
    maxLines: 1,
  },
  {
    label: 'text-h-s',
    value: 'mt-1 text-body-xs',
    labelPx: 14,
    valuePx: 12,
    labelLine: 20,
    valueLine: 20,
    minW: 90,
    minH: 60,
    maxLines: 2,
  },
  {
    label: 'text-h-xs',
    value: 'text-body-xs',
    labelPx: 12,
    valuePx: 12,
    labelLine: 16,
    valueLine: 16,
    minW: 40,
    minH: 36,
    maxLines: 2,
  },
]

/** The frames' text box sits 6px inside the tile on each side. */
const LABEL_PAD = 6

/**
 * A character's advance as a share of the font size. IBM Plex Sans measures
 * 0.46–0.59 across the frames' labels (bold caps widest); 0.6 errs toward the
 * next step down, which clips nothing, and it is the value that puts
 * "Microfinance" and "Manufacturing" on the steps the frames give them.
 */
const CHAR_EM = 0.6

/** The name-only step: one line at the smallest size, with a tighter pad. */
const LABEL_ONLY = { px: 12, pad: 4, minH: 20 }

/**
 * Lines `text` wraps to at `px` in `width`, breaking at spaces only; Infinity
 * when one word alone is wider than the line, since CSS would then clip it.
 */
export const linesFor = (text: string, px: number, width: number): number => {
  const advance = px * CHAR_EM
  let lines = 1
  let used = 0
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const wordWidth = word.length * advance
    if (wordWidth > width) return Infinity
    const withWord = used === 0 ? wordWidth : used + advance + wordWidth
    if (withWord <= width) {
      used = withWord
    } else {
      lines += 1
      used = wordWidth
    }
  }
  return lines
}

/**
 * The largest type step whose name and figure both fit a `w × h` tile (its
 * visible size, gutter already taken), else the name alone on one line, else
 * nothing.
 */
export const heatmapLabelFor = (
  w: number,
  h: number,
  label: string,
  value: string,
): HeatmapLabel => {
  const inner = w - LABEL_PAD * 2

  for (const rung of HEATMAP_RUNGS) {
    if (w < rung.minW || h < rung.minH) continue
    const lines = linesFor(label, rung.labelPx, inner)
    if (lines > rung.maxLines) continue
    if (value.length > 0 && linesFor(value, rung.valuePx, inner) > 1) continue
    const textHeight = lines * rung.labelLine + (value.length > 0 ? rung.valueLine : 0)
    if (textHeight > h - LABEL_PAD * 2) continue
    return { print: 'all', label: rung.label, value: rung.value, lines: lines === 1 ? 1 : 2 }
  }

  if (h >= LABEL_ONLY.minH && linesFor(label, LABEL_ONLY.px, w - LABEL_ONLY.pad * 2) === 1) {
    return { print: 'label' }
  }
  return { print: 'none' }
}

/**
 * The share map (`Treemap`'s `share` variant): a part-of-a-whole map whose
 * every tile prints a name and a figure, laid out, labelled and sized here.
 */

/** What a share-map tile prints: its name, its figure and, where there is room, a caption. */
export interface ShareText {
  label: string
  value: string
  caption?: string | undefined
}

/** One of the share map's two type steps. */
export interface ShareTier {
  id: 'full' | 'compact'
  /** Type classes for the name and the figure. */
  label: string
  value: string
  labelPx: number
  valuePx: number
  labelLine: number
  valueLine: number
  /** Inset on every side, in pixels, and as the class that draws it. */
  pad: number
  inset: string
}

/**
 * Two steps and no more. Every tile that fits it prints at `full` — `h-s` over
 * `body-xs`, the same size on every tile of the map — and `compact` is the
 * fallback for a tile too small for that, not a third or fourth size picked by
 * how big a tile happens to be.
 */
export const SHARE_TIERS: readonly ShareTier[] = [
  {
    id: 'full',
    label: 'text-h-s',
    value: 'text-body-xs',
    labelPx: 14,
    valuePx: 12,
    labelLine: 20,
    valueLine: 16,
    pad: 8,
    inset: 'p-2',
  },
  {
    id: 'compact',
    label: 'text-h-xs',
    value: 'text-body-xs',
    labelPx: 12,
    valuePx: 12,
    labelLine: 16,
    valueLine: 16,
    pad: 4,
    inset: 'p-1',
  },
]

/** The caption line under a `full` tile's figure: `label-s` (12/16), 4px above it. */
const SHARE_CAPTION = { px: 12, line: 16, gap: 4 }

/**
 * Every share tile is a link, so none is drawn shorter than a finger: 44px,
 * the touch-target minimum. `compact`'s two lines would fit in 37.
 */
const SHARE_MIN_H = 44

export interface ShareLabel {
  tier: ShareTier
  /** Whether the caption fits under the figure as well. */
  caption: boolean
}

/** One line of `text` at `px`, in pixels — the same `CHAR_EM` estimate `linesFor` makes. */
const lineWidth = (text: string, px: number) => text.length * px * CHAR_EM

/**
 * The largest step whose name AND figure both fit a `w × h` tile (its visible
 * size, gutter already taken), each on one line; null when not even
 * `compact` does. Only `full` carries the caption, and only when it fits too.
 */
export const shareLabelFor = (w: number, h: number, text: ShareText): ShareLabel | null => {
  for (const tier of SHARE_TIERS) {
    const inner = w - tier.pad * 2
    const textHeight = tier.labelLine + tier.valueLine
    if (lineWidth(text.label, tier.labelPx) > inner) continue
    if (lineWidth(text.value, tier.valuePx) > inner) continue
    if (textHeight > h - tier.pad * 2 || h < SHARE_MIN_H) continue
    const caption =
      tier.id === 'full' &&
      text.caption !== undefined &&
      text.caption.length > 0 &&
      lineWidth(text.caption, SHARE_CAPTION.px) <= inner &&
      textHeight + SHARE_CAPTION.gap + SHARE_CAPTION.line <= h - tier.pad * 2
    return { tier, caption }
  }
  return null
}

/**
 * How tall the share map stands for `count` drawn tiles in a box `width` wide.
 *
 * The data sets the height, not a fixed frame: one tile gets a band, not a
 * 200px box with one word in the middle of it, and every tile the map has to
 * draw adds a step (`SHARE_STEP_H`) on top of a floor. A narrow box stands
 * taller for the same names — each drawn tile is owed about `SHARE_TILE_AREA`,
 * a `full` tile with its caption and some air — so a phone column a third the
 * width of the desktop one does not squeeze the same six names into slivers.
 * Snapped to the 8px grid and held under `max`, one screenful of a card.
 */
const SHARE_TILE_AREA = 136 * 104
const SHARE_STEP_H = 24
const SHARE_HEIGHT = { min: 96, base: 56, max: 256 }

export const shareMapHeight = (width: number, count: number): number => {
  if (width <= 0 || count <= 0) return SHARE_HEIGHT.min
  const byCount = SHARE_HEIGHT.base + count * SHARE_STEP_H
  const byArea = (count * SHARE_TILE_AREA) / width
  const snapped = Math.round(Math.max(byCount, byArea) / 8) * 8
  return Math.min(SHARE_HEIGHT.max, Math.max(SHARE_HEIGHT.min, snapped))
}

/**
 * Where the share map's tiles go and what each prints.
 *
 * `named` tiles, from the top of the sorted list, keep their own name; the
 * rest are drawn as ONE pooled tile after them. `rects` are in a box `gap`
 * wider and taller than the map (see `layoutFlushRows`), `labels` say what
 * each drawn tile — the pool last — can print.
 */
export interface SharePlan {
  height: number
  named: number
  rects: Rect[]
  labels: (ShareLabel | null)[]
}

/**
 * The size a tile needs for `text` at `compact`, gutter included — the
 * smallest a tile can be and still say what it is.
 */
const compactSlot = (text: ShareText, gap: number) => {
  const tier = SHARE_TIERS[SHARE_TIERS.length - 1]
  if (tier === undefined) return { w: 0, h: 0 }
  return {
    w:
      Math.max(lineWidth(text.label, tier.labelPx), lineWidth(text.value, tier.valuePx)) +
      tier.pad * 2 +
      gap,
    h: Math.max(tier.labelLine + tier.valueLine + tier.pad * 2, SHARE_MIN_H) + gap,
  }
}

/** Every tile on its side: what a row layout of the transposed box becomes as columns. */
const transpose = (rect: Rect): Rect => ({ x: rect.y, y: rect.x, w: rect.h, h: rect.w })

/**
 * Past this many tiles the share map stops trying every arrangement and takes
 * `layoutRows`' — 2^(n−1) splits, twice, is 1,024 layouts at eleven tiles.
 */
const SHARE_EXHAUSTIVE_MAX = 10

/** Lays a set of areas out in a fixed box — one arrangement the share map can pick. */
type Arrangement = (areas: readonly number[]) => Rect[]

/**
 * `squarify`, then every way to cut `n` tiles, in order, into full-width rows
 * — and into full-height columns — each laid out flush in `box`. The share
 * map has a handful of tiles, so it can afford to try all of them and keep
 * the one whose words fit, where `squarify` and `layoutRows` optimise the
 * shape of tiles alone and hand a phone column four 70px tiles in a row. Area
 * stays exactly as given in every one.
 */
const arrangements = (n: number, box: Rect, gap: number): Arrangement[] => {
  const out: Arrangement[] = [(areas) => squarify(areas, box)]
  if (n > SHARE_EXHAUSTIVE_MAX) return [...out, (areas) => layoutRows(areas, box, gap)]

  const flipped = { x: box.y, y: box.x, w: box.h, h: box.w }
  for (let cuts = 0; cuts < 2 ** Math.max(n - 1, 0); cuts += 1) {
    const rows: [number, number][] = []
    let from = 0
    for (let k = 1; k < n; k += 1) {
      if ((cuts >> (k - 1)) & 1) {
        rows.push([from, k])
        from = k
      }
    }
    rows.push([from, n])
    out.push((areas) => placeRows(areas, box, rows))
    out.push((areas) => placeRows(areas, flipped, rows).map(transpose))
  }
  return out
}

/** How far a `w × h` tile is from square — 1 is square. */
const squareness = (rect: Rect, gap: number) => aspect(rect.w - gap, rect.h - gap)

interface Scored {
  rects: Rect[]
  labels: (ShareLabel | null)[]
  /** Tiles that print nothing. */
  blank: number
  /** How many times its true area the last tile is drawn at — 1 when it was not grown. */
  growth: number
  /** Pairs where the bigger tile prints at a smaller step than the smaller one. */
  inversions: number
  /** The whole ranking, most important first; lower is better at every place. */
  score: readonly number[]
}

/** Lexicographic: the smaller wins at the first place two scores differ. */
const better = (a: readonly number[], b: readonly number[]) => {
  for (let i = 0; i < a.length; i += 1) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0)
    if (Math.abs(diff) > 1e-9) return diff < 0
  }
  return false
}

/** How many times the last tile is grown toward its label before an arrangement gives up. */
const TAIL_STEPS = 8

/**
 * One arrangement of `honest` areas, its last tile grown if that is the only
 * way its label fits, scored: fewest tiles printing nothing, then the least
 * growth, then no bigger tile in smaller type than a smaller one, then the
 * most tiles at `full`, then the most captions, then the tiles closest to
 * square.
 */
const scoreArrangement = (
  honest: readonly number[],
  cut: Arrangement,
  texts: readonly ShareText[],
  boxArea: number,
  gap: number,
): Scored => {
  const last = honest.length - 1
  const tailText = texts[last] ?? { label: '', value: '' }
  const honestTail = honest[last] ?? 0

  let rects = cut(honest)
  let tailArea = honestTail
  for (let step = 0; step < TAIL_STEPS; step += 1) {
    const tail = rects[last]
    if (tail === undefined || shareLabelFor(tail.w - gap, tail.h - gap, tailText) !== null) break
    // Grown by how far short it falls on its worse side, so a full-height
    // column widens and a full-width strip deepens.
    const slot = compactSlot(tailText, gap)
    const next =
      tailArea * Math.max(slot.w / Math.max(tail.w, 1), slot.h / Math.max(tail.h, 1), 1.05)
    if (next > boxArea / 2) break
    tailArea = next
    const restScale = boxArea > honestTail ? (boxArea - tailArea) / (boxArea - honestTail) : 0
    rects = cut(honest.map((area, i) => (i === last ? tailArea : area * restScale)))
  }

  const labels = rects.map((rect, i) =>
    shareLabelFor(rect.w - gap, rect.h - gap, texts[i] ?? { label: '', value: '' }),
  )
  const blank = labels.filter((label) => label === null).length
  const growth = honestTail > 0 ? tailArea / honestTail : 1
  const worstSquareness = rects.reduce((acc, rect) => Math.max(acc, squareness(rect, gap)), 1)
  // A tile printing at a smaller step than a smaller tile beside it — the
  // big name in small type — reads as a mistake, whatever the totals say.
  const rank = labels.map((label) => (label === null ? 0 : label.tier.id === 'full' ? 2 : 1))
  let inversions = 0
  for (let i = 0; i < rank.length; i += 1) {
    for (let j = i + 1; j < rank.length; j += 1) {
      if ((honest[i] ?? 0) > (honest[j] ?? 0) && (rank[i] ?? 0) < (rank[j] ?? 0)) inversions += 1
    }
  }
  return {
    rects,
    labels,
    blank,
    growth,
    inversions,
    score: [
      blank,
      growth,
      inversions,
      -labels.filter((label) => label?.tier.id === 'full').length,
      -labels.filter((label) => label?.caption === true).length,
      worstSquareness,
    ],
  }
}

/**
 * Tile area stays linear to the share. A name that would print nothing at its
 * true size is not drawn as an unlabelled sliver: the smallest names are
 * pooled into one tile after the rest — only when two or more would not fit,
 * since a pool of one is just that name — and the plan keeps as many names as
 * it can, in whichever arrangement of rows or columns their words fit.
 *
 * The one exception to linear area is that last tile, and only when nothing
 * else works. On a skewed group it cannot be read at its true size whatever
 * is pooled into it — 4% of a 791px-wide map is a 32px column — so it alone is
 * grown, taking the area back from the rest in proportion, until its label
 * fits. A plan that needs no growth always wins over one that does. Whoever
 * draws the map has to say so; the share it prints stays the true one.
 *
 * `weights` must be sorted, biggest first. `textFor(i)` is what named tile
 * `i` prints and `poolTextFor(from)` what a pool of every name from `from`
 * on would.
 */
export const planShareMap = (
  weights: readonly number[],
  textFor: (index: number) => ShareText,
  poolTextFor: (from: number) => ShareText,
  width: number,
  gap: number,
): SharePlan => {
  const n = weights.length
  const empty = { height: shareMapHeight(width, 0), named: 0, rects: [], labels: [] }
  if (n === 0 || width <= 0) return empty

  const clean = weights.map((weight) => Math.max(weight, 0))
  const candidates = [n]
  for (let named = n - 2; named >= 1; named -= 1) candidates.push(named)

  const plans = candidates.map((named) => {
    const drawn =
      named < n ? [...clean.slice(0, named), clean.slice(named).reduce((a, b) => a + b, 0)] : clean
    const texts = drawn.map((_, i) => (i < named ? textFor(i) : poolTextFor(named)))
    const height = shareMapHeight(width, drawn.length)
    const box = { x: 0, y: 0, w: width + gap, h: height + gap }
    const boxArea = box.w * box.h
    const total = drawn.reduce((a, b) => a + b, 0)
    const honest = drawn.map((weight) =>
      total > 0 ? (weight / total) * boxArea : boxArea / drawn.length,
    )

    let best: Scored | null = null
    for (const cut of arrangements(drawn.length, box, gap)) {
      const scored = scoreArrangement(honest, cut, texts, boxArea, gap)
      if (best === null || better(scored.score, best.score)) best = scored
    }
    return { plan: { height, named, rects: best?.rects ?? [], labels: best?.labels ?? [] }, best }
  })

  // The most names whose plan prints every tile, grows nothing and puts no
  // big name in small type; then the same with growth; then any that prints
  // every tile; then whichever leaves the fewest blank.
  const passes: ((scored: Scored) => boolean)[] = [
    ({ blank, growth, inversions }) => blank === 0 && growth <= 1 && inversions === 0,
    ({ blank, inversions }) => blank === 0 && inversions === 0,
    ({ blank }) => blank === 0,
  ]
  for (const pass of passes) {
    const found = plans.find(({ best }) => best !== null && pass(best))
    if (found !== undefined) return found.plan
  }
  let fewest = plans[0]
  for (const candidate of plans) {
    if ((candidate.best?.blank ?? Infinity) < (fewest?.best?.blank ?? Infinity)) fewest = candidate
  }
  return fewest?.plan ?? empty
}
